import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import {
  ref,
  onValue,
  update,
  push,
  set,
  serverTimestamp,
  remove,
} from "firebase/database";
import { useAuth } from "../../components/AuthContext";

import {
  FaUser,
  FaCalendarAlt,
  FaReply,
  FaPaperPlane,
  FaTrashAlt,
  FaArrowLeft,
  FaExclamationTriangle,
  FaImage,
  FaEdit,
} from "react-icons/fa";

const statusOptions = [
  {
    value: "접수",
    label: "접수",
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  {
    value: "처리중",
    label: "처리중",
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  {
    value: "완료",
    label: "완료",
    color: "bg-green-100 text-green-800 border-green-200",
  },
];

const SupportTicketView = () => {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { user, isAdmin } = useAuth();
  const adminStatus = isAdmin;
  const navigate = useNavigate();

  // 티켓 데이터 로드
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const ticketRef = ref(database, `supportTickets/${ticketId}`);
    onValue(ticketRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setTicket(data);
        setPreviousStatus(data.status || "접수");
      } else {
        navigate("/support");
      }
      setIsLoading(false);
    });

    // 댓글 데이터 로드
    const commentsRef = ref(database, `supportTickets/${ticketId}/comments`);
    onValue(commentsRef, (snapshot) => {
      const data = snapshot.val();
      const commentList = data
        ? Object.entries(data).map(([id, value]) => ({ id, ...value }))
        : [];
      setComments(
        commentList.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        )
      );
    });
  }, [ticketId, navigate, user]);

  // 댓글 제출 함수
  const submitComment = async () => {
    if (!newComment.trim()) return;

    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    const commentsRef = ref(database, `supportTickets/${ticketId}/comments`);
    const newCommentRef = push(commentsRef);

    await set(newCommentRef, {
      content: newComment,
      author: user.email,
      authorName: user.displayName || user.email.split("@")[0],
      createdAt: formattedDate,
      timestamp: serverTimestamp(),
    });

    // 댓글 수 업데이트
    const ticketRef = ref(database, `supportTickets/${ticketId}`);
    update(ticketRef, {
      commentCount: (ticket.commentCount || 0) + 1,
    });

    setNewComment("");
  };

  // 완료 이메일 발송 함수
  const sendCompletionEmail = async () => {
    if (!ticket) return;

    setIsEmailSending(true);

    try {
      const response = await fetch(
        process.env.REACT_APP_EMAIL_API_URL || "https://sendsupportticketemail-hxxheppfsq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": process.env.REACT_APP_EMAIL_API_KEY || "om8B2QikaJvIImlhlXTw",
          },
          body: JSON.stringify({
            to: ticket.author,
            name: ticket.authorName || ticket.author.split("@")[0],
            subject: `[완료] ${ticket.title} - 지원 요청이 완료되었습니다`,
            ticketId: ticketId,
            ticketTitle: ticket.title,
            ticketLink: `${window.location.origin}/support/${ticketId}`,
            emailType: "completed", // 완료 템플릿 사용
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("완료 이메일 발송 성공:", result);

      // 이메일 발송 상태 업데이트
      const ticketRef = ref(database, `supportTickets/${ticketId}`);
      await update(ticketRef, {
        completionEmailSent: true,
        completionEmailSentAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("완료 이메일 발송 실패:", error);
      alert("완료 이메일 발송에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsEmailSending(false);
    }
  };

  // 상태 변경 함수
  const handleStatusChange = async (newStatus) => {
    const ticketRef = ref(database, `supportTickets/${ticketId}`);

    await update(ticketRef, {
      status: newStatus,
    });

    // 상태가 '완료'로 변경되었을 때 이메일 발송
    if (newStatus === "완료" && previousStatus !== "완료") {
      await sendCompletionEmail();
    }

    setPreviousStatus(newStatus);
  };

  // 티켓 삭제 함수
  const deleteTicket = async () => {
    if (!user || (!adminStatus && ticket.author !== user.email)) return;

    setIsDeleting(true);
    try {
      const ticketRef = ref(database, `supportTickets/${ticketId}`);
      await remove(ticketRef);
      navigate("/support");
    } catch (error) {
      console.error("티켓 삭제 중 오류 발생:", error);
      alert("티켓을 삭제하는 중 오류가 발생했습니다.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-gray-800">
          티켓을 찾을 수 없습니다
        </h2>
        <button
          onClick={() => navigate("/support")}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  const canDelete = adminStatus || ticket.author === user.email;
  const canEdit = adminStatus || ticket.author === user.email;

  return (
    <div className="bg-gradient-to-b from-gray-50 to-blue-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* 상단 네비게이션 */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/support")}
            className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            <span className="font-medium">지원 게시판으로 돌아가기</span>
          </button>
        </div>

        {/* 티켓 카드 */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8 transform transition-all duration-300 hover:shadow-2xl">
          {/* 티켓 헤더 */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 px-6 py-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white bg-opacity-20 text-black mb-2">
                  문의 #{ticketId.substring(0, 6)}
                </div>
                <h1 className="text-2xl font-bold text-white mb-2 text-left">
                  {ticket.title}
                </h1>
                <div className="flex items-center text-sm text-white text-opacity-90">
                  <FaUser className="mr-1" />
                  <span className="mr-3">
                    {ticket.authorName || (
                      <>
                        <span className="font-medium">
                          {ticket.author.split("@")[0]}
                        </span>
                        <span className="opacity-75">
                          @{ticket.author.split("@")[1]}
                        </span>
                      </>
                    )}
                  </span>
                  <FaCalendarAlt className="mr-1" />
                  <span>{ticket.createdAt}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 티켓 내용 */}
          <div className="px-6 py-6">
            <div className="prose max-w-none text-gray-800 min-h-64">
              {ticket.content.split("\n").map((line, i) =>
                line ? (
                  <p key={i} className="mb-4">
                    {line}
                  </p>
                ) : (
                  <br key={i} />
                )
              )}
            </div>
          </div>

          {/* 첨부파일 섹션 */}
          {ticket.imageUrls && ticket.imageUrls.length > 0 && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaImage className="mr-2 text-indigo-500" />
                첨부파일 ({ticket.imageUrls.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ticket.imageUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`첨부 이미지 ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-200"
                      onClick={() => {
                        setShowImageModal(true);
                        setSelectedImageIndex(index);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 삭제/수정 버튼 및 상태 변경 */}
          <div className="px-6 pb-6 flex justify-end items-center space-x-4 border-t border-gray-100 pt-4">
            {canEdit && (
              <button
                onClick={() => navigate(`/support/edit/${ticketId}`)}
                className="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 rounded-md text-blue-700 transition-colors duration-200"
              >
                <FaEdit className="mr-1.5 h-3.5 w-3.5" />
                수정
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 rounded-md text-red-700 transition-colors duration-200"
                disabled={isDeleting}
              >
                <FaTrashAlt className="mr-1.5 h-3.5 w-3.5" />
                삭제
              </button>
            )}

            {adminStatus ? (
              <div className="relative">
                <select
                  value={ticket.status || "접수"}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="block w-full pl-3 pr-10 py-1.5 text-sm text-gray-900 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={isEmailSending}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}{" "}
                      {isEmailSending && option.value === "완료"
                        ? "(발송 중...)"
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  statusOptions.find(
                    (opt) => opt.value === (ticket.status || "접수")
                  )?.color
                }`}
              >
                {ticket.status || "접수"}
              </span>
            )}
          </div>
        </div>

        {/* 댓글 섹션 */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
          <div className="border-b border-gray-200 px-6 py-4 bg-indigo-50">
            <h2 className="text-lg font-bold text-gray-900">
              댓글 ({comments.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {comments.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <FaReply className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <p className="text-lg font-medium mb-1">
                  첫 번째 댓글을 남겨보세요
                </p>
                <p className="text-sm text-gray-400">
                  질문이나 의견을 공유해주세요
                </p>
              </div>
            ) : (
              comments.map((comment) => (
                <div
                  key={comment.id}
                  className="px-6 py-5 hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center">
                        <span className="text-white font-medium">
                          {comment.authorName
                            ? comment.authorName[0].toUpperCase()
                            : "U"}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <div className="flex items-center">
                          <p className="font-medium text-gray-900">
                            {comment.authorName || (
                              <>
                                <span>{comment.author.split("@")[0]}</span>
                                <span className="text-xs text-gray-500 ml-1">
                                  @{comment.author.split("@")[1]}
                                </span>
                              </>
                            )}
                          </p>
                          {ticket.author === comment.author && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                              작성자
                            </span>
                          )}
                          {adminStatus && ticket.author !== comment.author && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                              관리자
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {comment.createdAt}
                        </p>
                      </div>
                      <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                        {comment.content}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 댓글 작성 폼 */}
          <div className="px-6 py-6 bg-gradient-to-b from-white to-blue-50 border-t border-gray-200">
            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-600 to-blue-500 flex items-center justify-center shadow-md transform transition-transform hover:scale-105 duration-300">
                  <span className="text-white font-bold text-lg">
                    {user
                      ? user.displayName
                        ? user.displayName[0].toUpperCase()
                        : user.email[0].toUpperCase()
                      : "U"}
                  </span>
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <textarea
                  rows="4"
                  className="shadow-lg block w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base border border-gray-300 rounded-xl transition-colors duration-200 py-3 px-4 resize-none placeholder-gray-400"
                  placeholder="의견이나 질문을 작성해주세요..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                ></textarea>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    className={`inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-xl shadow-lg text-white 
                      ${
                        !newComment.trim()
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-indigo-600 to-blue-500 hover:from-indigo-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-300 transform hover:scale-105"
                      }`}
                    onClick={submitComment}
                    disabled={!newComment.trim()}
                  >
                    <FaPaperPlane className="mr-2 h-4 w-4" />
                    댓글 등록하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 삭제 확인 모달 */}
        {showDeleteModal && (
          <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowDeleteModal(false)}
            ></div>
            <div className="relative bg-white rounded-lg max-w-md w-full mx-auto p-6 shadow-xl transform transition-all sm:p-6">
              <div className="text-center sm:text-left">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0">
                  <FaExclamationTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    문의 삭제
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      이 문의를 정말 삭제하시겠습니까? 이 작업은 취소할 수
                      없으며 모든 댓글도 함께 삭제됩니다.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:text-sm"
                  onClick={deleteTicket}
                  disabled={isDeleting}
                >
                  {isDeleting ? "삭제 중..." : "삭제"}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:text-sm"
                  onClick={() => setShowDeleteModal(false)}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 이미지 확대 모달 */}
        {showImageModal && ticket.imageUrls && ticket.imageUrls.length > 0 && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setShowImageModal(false)}
          >
            <div className="relative max-w-4xl max-h-full p-4">
              <img
                src={ticket.imageUrls[selectedImageIndex]}
                alt={`확대된 첨부 이미지 ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <button
                className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(false);
                }}
              >
                ×
              </button>
              {ticket.imageUrls.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white text-3xl bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) =>
                        prev > 0 ? prev - 1 : ticket.imageUrls.length - 1
                      );
                    }}
                  >
                    ‹
                  </button>
                  <button
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white text-3xl bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) =>
                        prev < ticket.imageUrls.length - 1 ? prev + 1 : 0
                      );
                    }}
                  >
                    ›
                  </button>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex + 1} / {ticket.imageUrls.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketView;